"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getCroppedBlob } from "@/lib/cropImage";
import { MAX_UPLOAD_SIZE_MB, fileTooLargeMessage } from "@/lib/upload-limits";
import { toast } from "@/lib/toast";

function loadImageDims(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

export interface UseSquareEmojiImage {
  /** Preview src: a blob: object URL for cropped picks, or a pasted link. */
  preview: string | null;
  /** The cropped image awaiting upload; null when the preview is a pasted link. */
  blob: Blob | null;
  isCropping: boolean;
  cropFile: (file: File) => Promise<void>;
  clear: () => void;
}

/**
 * Pick/paste → auto center-crop pipeline for custom emojis (128px square
 * PNG). While `pasteEnabled`, ⌘/Ctrl+V accepts a clipboard image (cropped
 * like a picked file) or an http(s) link (used as-is, skipping crop).
 */
export function useSquareEmojiImage({ pasteEnabled = false }: { pasteEnabled?: boolean } = {}): UseSquareEmojiImage {
  const [preview, setPreview] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const clear = useCallback(() => {
    revokePreviewUrl();
    setPreview(null);
    setBlob(null);
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const cropFile = useCallback(async (file: File) => {
    if (!file.type.includes("image/")) {
      toast.error("File type not supported. Please upload an image file.");
      return;
    }
    if (file.size / 1024 / 1024 > MAX_UPLOAD_SIZE_MB) {
      toast.error(fileTooLargeMessage(MAX_UPLOAD_SIZE_MB));
      return;
    }
    setIsCropping(true);
    const src = URL.createObjectURL(file);
    try {
      const { width, height } = await loadImageDims(src);
      const side = Math.min(width, height);
      const cropped = await getCroppedBlob(
        src,
        { x: (width - side) / 2, y: (height - side) / 2, width: side, height: side },
        { maxSize: 128, mimeType: "image/png" },
      );
      revokePreviewUrl();
      const url = URL.createObjectURL(cropped);
      previewUrlRef.current = url;
      setBlob(cropped);
      setPreview(url);
    } catch (err) {
      toast.error((err as Error).message || "Failed to process image");
    } finally {
      URL.revokeObjectURL(src);
      setIsCropping(false);
    }
  }, []);

  useEffect(() => {
    if (!pasteEnabled) return;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (items) {
        for (const item of Array.from(items)) {
          if (item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) {
              e.preventDefault();
              void cropFile(file);
              return;
            }
          }
        }
      }
      // Don't hijack text pastes aimed at an input (e.g. the name field).
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      const text = e.clipboardData?.getData("text/plain")?.trim();
      if (text && /^https?:\/\/\S+$/i.test(text)) {
        e.preventDefault();
        revokePreviewUrl();
        setBlob(null);
        setPreview(text);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [pasteEnabled, cropFile]);

  return { preview, blob, isCropping, cropFile, clear };
}
